import { useEffect, useRef, useState } from "react";
import type { SalesforceChatConfig, ChatMessage, ChatSession, ParsedEntryPayload } from "../types/chat";
import { fetchEventSource } from "@microsoft/fetch-event-source";

export function useChatStream(config: SalesforceChatConfig, session?: ChatSession) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [connected, setConnected] = useState(false);
  const [typing, setTyping] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!session?.conversationId || !session.accessToken) return;

    abortRef.current?.abort();
    setConnected(false);
    setTyping(false);

    const controller = new AbortController();
    abortRef.current = controller;

    const url = `${config.scrtUrl}/eventrouter/v1/sse?conversationId=${encodeURIComponent(
      session.conversationId
    )}`;

    fetchEventSource(url, {
      method: "GET",
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${session.accessToken}`,
        Accept: "text/event-stream",
        "Content-Type": "application/json",
        "X-Org-Id": config.orgId,
      },

      onopen: async (resp) => {
        if (resp.ok) {
          setConnected(true);
        } else {
          console.error("Falha ao conectar SSE:", resp.status, resp.statusText);
        }
      },

      onmessage(msg) {
        if (!msg?.data) return;

        let raw: any;
        try {
          raw = JSON.parse(msg.data);
        } catch {
          return;
        }

        if (raw.conversationEntry) {
          const entry = raw.conversationEntry;
          const senderRole =
            entry?.sender?.role?.toLowerCase().includes("chatbot") ? "agent" : "agent";

          let payload: ParsedEntryPayload | null = null;
          try {
            payload = JSON.parse(entry.entryPayload) as ParsedEntryPayload;
          } catch {
            console.warn("Erro ao parsear entryPayload");
          }

          const abstract = payload?.abstractMessage;
          if (!abstract) return;

          if (abstract.messageType === "StaticContentMessage") {
            const text =
              abstract?.staticContent?.text ??
              abstract?.text ??
              null;

            if (text) {
              setMessages((prev) => [
                ...prev,
                {
                  type: "Text",
                  text,
                  from: senderRole,
                },
              ]);
            }
          }

          if (abstract.messageType === "ChoicesMessage" && abstract.choices) {
            const q = abstract.choices;

            const questionText = q.text ?? "";

            const choices =
              q.optionItems?.map((item) => ({
                label: item?.titleItem?.title ?? "Opção",
                optionIdentifier: item?.optionIdentifier ?? "",
              })) ?? [];

            setMessages((prev) => [
              ...prev,
              {
                type: "Choices",
                text: questionText,
                choices,
                from: senderRole,
              },
            ]);
          }

          if (abstract.structuredContent) {
            const sc = abstract.structuredContent;

            setMessages((prev) => [
              ...prev,
              {
                type: "Rich",
                text: sc.text ?? undefined,
                cards: sc.cards ?? undefined,
                buttons: sc.buttons ?? undefined,
                from: senderRole,
              },
            ]);
          }

          return;
        }

        if (raw.eventType === "CONVERSATION_AGENT_TYPING_STARTED") {
          setTyping(true);
          return;
        }

        if (raw.eventType === "CONVERSATION_AGENT_TYPING_STOPPED") {
          setTyping(false);
          return;
        }

        if (!raw.conversationEntry) {
          const fallbackText = raw?.message?.payload?.text;
          if (typeof fallbackText === "string") {
            setMessages((prev) => [
              ...prev,
              { type: "Text", text: fallbackText, from: "agent" },
            ]);
          }
        }
      },

      onerror(err) {
        console.error("SSE erro:", err);
        setConnected(false);
      },

      onclose() {
        setConnected(false);
        setTyping(false);
      },
    });

    return () => {
      controller.abort();
      setConnected(false);
      setTyping(false);
    };
  }, [session?.conversationId]);

  return {
    messages,
    connected,
    typing,
  };
}
